import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { Component, useId } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from '@liveauctioneers/hammer-ui-core/intl';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { Select } from '@liveauctioneers/hammer-ui-core/select';
import isEqual from 'react-fast-compare';

export type SearchSortProps = {
    archivedSearch: boolean;
    className?: string;
    instanceId: ReturnType<typeof useId>;
    sort: SearchSortAndDirection;
    sortOptions: SearchSortAndDirection[];
    sortSelected: (option: string) => void;
};

type State = {
    selectedSortOption: SearchSortAndDirection;
};

// intl is being injected. for our tests, we don't want to mess with the intl object,
// so I moved it out of the SearchSortProps
class SearchSort extends Component<SearchSortProps & { intl: IntlShape }, State> {
    state = {
        selectedSortOption: this.props.sort,
    };

    shouldComponentUpdate = (nextProps, nextState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps) {
        if (this.props.sortOptions.length !== prevProps.sortOptions.length) {
            this.setState({ selectedSortOption: this.props.sortOptions[0] });
        }
        if (this.props.sort !== prevProps.sort) {
            this.setState({ selectedSortOption: this.props.sort });
        }
    }

    onChangeSortOption = (event) => {
        const selectedSortOption = event.value;
        this.setState({ selectedSortOption });
        this.props.sortSelected(selectedSortOption);
    };

    mapSortOptions = (sort: SearchSortAndDirection) => {
        // ? fix referenced in `sortTypes` (Recent vs Soonest)
        // Time: Most Recent appears for past items, Time: Starting Soonest for upcoming items
        if (!this.props.archivedSearch && sort === SearchSortAndDirection.TimeMostRecent) {
            return {
                label: this.props.intl.formatMessage({
                    id: `sorts.${SearchSortAndDirection.TimeStartingSoonest}`,
                }),
                value: sort,
            };
        }
        // ? fix referenced in `sortTypes` (Newest vs Soonest)
        // on Search > Price Results, rename Time: Newest to Time: Most Recent to be consistent everywhere else on the site
        if (this.props.archivedSearch && sort === SearchSortAndDirection.TimeNewest) {
            return {
                label: this.props.intl.formatMessage({
                    id: `sorts.${SearchSortAndDirection.TimeMostRecent}`,
                }),
                value: sort,
            };
        }

        const message = this.props.intl.formatMessage({ id: `sorts.${sort}` });

        return {
            label: message,
            value: sort,
        };
    };

    render() {
        const { className, intl, sortOptions } = this.props;
        const { selectedSortOption } = this.state;
        const displaySortOptions = sortOptions.length > 0;
        const sOptions = sortOptions.map(this.mapSortOptions);

        return displaySortOptions ? (
            <>
                <Select
                    className={cn('xsMin:ml-lg', className)}
                    instanceId="search-sort"
                    isInsideLabel
                    isSearchable={false}
                    labelText={<FormattedMessage id="search_sort" />}
                    name="sort"
                    onChange={this.onChangeSortOption}
                    options={sOptions}
                    placeholder={intl.formatMessage({ id: 'select' })}
                    small
                    value={sOptions.find((op) => op.value === selectedSortOption)}
                />
            </>
        ) : null;
    }
}

export default injectIntl(SearchSort);
